import { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import dayjs from "dayjs";
import {
    ArrowLeftOutlined,
    DeleteOutlined,
    EditOutlined,
    LinkOutlined,
    PlusOutlined,
    ReloadOutlined,
} from "@ant-design/icons";
import {
    Avatar,
    Button,
    Card,
    Descriptions,
    Empty,
    Flex,
    Image,
    Popconfirm,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography,
    type DescriptionsProps,
    type TableProps,
} from "antd";

import { CustomResult } from "@repo/ui";
import {
    buildKaspiProductLink,
    buildProductImageUrl,
    formatPrice,
} from "@src/components/merchant_products/utils";
import { CostPriceDrawer } from "@src/components/merchant_products";
import { useCostPriceDelete, useCostPrices, useMerchantProductDetail } from "@src/queries";
import type {
    CostPriceType,
    MerchantProductAvailability,
    MerchantProductCityPrice,
} from "@src/types";

const { Title, Paragraph, Text, Link } = Typography;
const { PreviewGroup } = Image;
const DATE_FORMAT = "DD.MM.YYYY HH:mm";

function formatDateTime(value?: string | null) {
    return value ? dayjs(value).format(DATE_FORMAT) : "—";
}

function formatNumber(value?: number | null) {
    if (typeof value === "number") {
        return value.toLocaleString("ru-RU");
    }
    return "—";
}

function formatWarehouseAddress(warehouse?: MerchantProductAvailability["warehouse"] | null) {
    if (!warehouse) return null;
    const street = warehouse.street_name?.trim();
    const number = warehouse.street_number?.trim();
    const building = warehouse.building?.trim();
    const base = [street, number].filter(Boolean).join(", ");

    if (building && base) {
        return `${base} (${building})`;
    }

    if (building && !base) {
        return building;
    }

    return base || null;
}

export const MerchantProductDetailPage = () => {
    const { merchantProductId } = useParams<{ merchantProductId: string }>();
    const navigate = useNavigate();

    const detailQuery = useMerchantProductDetail({
        merchantProductId,
        enabled: Boolean(merchantProductId),
    });
    const detail = detailQuery.data;

    const costPricesQuery = useCostPrices({
        merchantProductId,
        enabled: Boolean(merchantProductId),
    });
    const costPrices = costPricesQuery.data ?? [];

    const costPriceDeleteMutation = useCostPriceDelete(merchantProductId ?? "");

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [selectedCostPrice, setSelectedCostPrice] = useState<CostPriceType | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const handleDrawerClose = useCallback(() => {
        setIsDrawerOpen(false);
        setSelectedCostPrice(null);
    }, []);

    const handleAddCostPrice = useCallback(() => {
        setSelectedCostPrice(null);
        setIsDrawerOpen(true);
    }, []);

    const handleEditCostPrice = useCallback((record: CostPriceType) => {
        setSelectedCostPrice(record);
        setIsDrawerOpen(true);
    }, []);

    const handleDeleteCostPrice = useCallback(
        (record: CostPriceType) => {
            const costPriceId = record.id;
            setDeletingId(costPriceId);
            costPriceDeleteMutation.mutate(
                { costPriceId },
                {
                    onSettled: () => {
                        setDeletingId((current) => (current === costPriceId ? null : current));
                    },
                },
            );
        },
        [costPriceDeleteMutation],
    );

    const imageUrls = useMemo(() => {
        if (!detail?.product?.images?.length) return [];
        return detail.product.images
            .map((image) => buildProductImageUrl(image))
            .filter((url): url is string => Boolean(url));
    }, [detail?.product?.images]);

    const shopLink = buildKaspiProductLink(detail?.product?.shop_link);

    const costPriceColumns = useMemo<TableProps<CostPriceType>["columns"]>(
        () => [
            {
                title: "Начало действия",
                dataIndex: "active_from",
                key: "active_from",
                render: (value: string) => formatDateTime(value),
            },
            {
                title: "Себестоимость",
                dataIndex: "cost_price",
                key: "cost_price",
                render: (value: number) => formatPrice(value),
            },
            {
                title: "Действия",
                key: "actions",
                render: (_value, record) => {
                    const isDeleting =
                        costPriceDeleteMutation.isPending && deletingId === record.id;
                    return (
                        <Space size="small">
                            <Button
                                type="link"
                                size="small"
                                icon={<EditOutlined />}
                                onClick={() => handleEditCostPrice(record)}
                            />
                            <Popconfirm
                                title="Удалить себестоимость?"
                                okText="Удалить"
                                cancelText="Отмена"
                                placement="left"
                                onConfirm={() => handleDeleteCostPrice(record)}
                                okButtonProps={{ loading: isDeleting }}
                                disabled={isDeleting}
                            >
                                <Button
                                    type="link"
                                    danger
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    loading={isDeleting}
                                />
                            </Popconfirm>
                        </Space>
                    );
                },
            },
        ],
        [costPriceDeleteMutation.isPending, deletingId, handleDeleteCostPrice, handleEditCostPrice],
    );

    const availabilityColumns = useMemo<TableProps<MerchantProductAvailability>["columns"]>(
        () => [
            {
                title: "Склад",
                dataIndex: ["warehouse", "name"],
                key: "warehouse",
                render: (_value, record) => {
                    const name = record.warehouse?.name ?? "—";
                    const address = formatWarehouseAddress(record.warehouse);
                    const content = (
                        <span className={address ? "cursor-help" : undefined}>{name}</span>
                    );
                    return address ? <Tooltip title={address}>{content}</Tooltip> : content;
                },
            },
            {
                title: "Доступно",
                dataIndex: "available",
                key: "available",
                render: (value: string) => {
                    const normalized = value?.toLowerCase() ?? "";
                    const isAvailable = normalized.includes("available") || normalized === "yes";
                    return <Tag color={isAvailable ? "green" : "default"}>{value || "—"}</Tag>;
                },
            },
            {
                title: "Остаток",
                dataIndex: "stock_count",
                key: "stock_count",
                render: (value?: number | null) => formatNumber(value),
            },
            {
                title: "Предзаказ",
                dataIndex: "pre_order",
                key: "pre_order",
                render: (value?: number | null) => formatNumber(value),
            },
            {
                title: "Обновлено",
                dataIndex: "updated_at",
                key: "updated_at",
                render: (value: string) => formatDateTime(value),
            },
        ],
        [],
    );

    const isCostPriceCardLoading = costPricesQuery.isLoading && !costPricesQuery.data;
    const isCostPriceTableLoading = costPricesQuery.isFetching && !isCostPriceCardLoading;

    const cityPriceColumns = useMemo<TableProps<MerchantProductCityPrice>["columns"]>(
        () => [
            {
                title: "Город",
                dataIndex: ["city", "name"],
                key: "city",
                render: (_value, record) => record.city?.name ?? "—",
            },
            {
                title: "Цена",
                dataIndex: "price",
                key: "price",
                render: (value: number) => formatPrice(value),
            },
            {
                title: "Обновлено",
                dataIndex: "updated_at",
                key: "updated_at",
                render: (value: string) => formatDateTime(value),
            },
        ],
        [],
    );

    const infoItems: DescriptionsProps["items"] = useMemo(() => {
        if (!detail) {
            return [];
        }

        return [
            {
                key: "sku",
                label: "SKU",
                children: <Text copyable={{ text: detail.sku }}>{detail.sku ?? "—"}</Text>,
            },
            // {
            //     key: "merchant",
            //     label: "ID магазина",
            //     children: detail.merchant_id,
            // },
            {
                key: "cost-price",
                label: "Себестоимость",
                children: formatPrice(detail.current_cost_price),
            },
            {
                key: "price-range",
                label: "Цена",
                children:
                    detail.min_price === detail.max_price
                        ? formatPrice(detail.min_price)
                        : `${formatPrice(detail.min_price)} – ${formatPrice(detail.max_price)}`,
            },
            {
                key: "last-updated",
                label: "Обновлено в кабинете",
                children: formatDateTime(detail.last_updated_on_cabinet),
            },
        ];
    }, [detail]);

    const handleBackClick = () => navigate("/products");

    if (!merchantProductId) {
        return (
            <CustomResult
                type="error"
                title="Не указан товар"
                subTitle="Вернитесь в список товаров и попробуйте снова."
                withCard
                extra={
                    <Button type="primary" onClick={handleBackClick}>
                        К каталогу
                    </Button>
                }
            />
        );
    }

    if (detailQuery.isError) {
        return (
            <CustomResult
                type="error"
                title="Не удалось загрузить товар"
                axiosError={detailQuery.error}
                withCard
                extra={
                    <Space>
                        <Button icon={<ArrowLeftOutlined />} onClick={handleBackClick}>
                            К каталогу
                        </Button>
                        <Button
                            type="primary"
                            icon={<ReloadOutlined />}
                            onClick={() => detailQuery.refetch()}
                        >
                            Повторить
                        </Button>
                    </Space>
                }
            />
        );
    }

    return (
        <>
            <Flex vertical gap={24} style={{ width: "100%" }}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <Title level={2} style={{ margin: 0 }}>
                        Карточка товара
                    </Title>

                    <Space wrap>
                        <Button icon={<ArrowLeftOutlined />} onClick={handleBackClick}>
                            Назад к списку
                        </Button>
                    </Space>
                </div>

                <Card title="Основная информация" loading={detailQuery.isLoading}>
                    {detail ? (
                        <Flex gap={24} wrap>
                            {imageUrls.length ? (
                                <PreviewGroup items={imageUrls}>
                                    <Image
                                        src={imageUrls[0]}
                                        width={240}
                                        height={240}
                                        style={{ objectFit: "cover" }}
                                        className="rounded-lg"
                                        preview={{ mask: <span className="text-xs">Открыть</span> }}
                                    />
                                </PreviewGroup>
                            ) : (
                                <Avatar shape="square" size={240} className="bg-gray-100 text-5xl">
                                    {detail.title?.charAt(0)?.toUpperCase() ??
                                        detail.product?.title?.charAt(0)?.toUpperCase() ??
                                        "?"}
                                </Avatar>
                            )}

                            <div className="min-w-[280px] flex-1">
                                <Space size="small" wrap>
                                    <Tag color={detail.available ? "green" : "default"}>
                                        {detail.available ? "В наличии" : "Нет в наличии"}
                                    </Tag>
                                    {detail.brand ? (
                                        <Tag color="purple">Собственный бренд</Tag>
                                    ) : (
                                        <Tag color="blue">Без бренда</Tag>
                                    )}
                                    {detail.brand && <Tag>{detail.brand}</Tag>}
                                </Space>

                                <Title level={3} style={{ marginTop: 16, marginBottom: 8 }}>
                                    {detail.title || detail.product?.title || "Без названия"}
                                </Title>

                                {shopLink && (
                                    <Paragraph>
                                        <Link
                                            href={shopLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <LinkOutlined /> Страница товара на Kaspi
                                        </Link>
                                    </Paragraph>
                                )}

                                <Descriptions
                                    column={1}
                                    items={infoItems}
                                    styles={{ label: { width: 180 } }}
                                />
                            </div>
                        </Flex>
                    ) : (
                        <Empty description="Данные недоступны" />
                    )}
                </Card>

                <Card
                    title="История себестоимости"
                    loading={isCostPriceCardLoading}
                    extra={
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={handleAddCostPrice}
                            disabled={!merchantProductId || isCostPriceCardLoading}
                        >
                            Добавить запись
                        </Button>
                    }
                >
                    {costPricesQuery.isError ? (
                        <CustomResult
                            type="error"
                            title="Не удалось загрузить себестоимость"
                            axiosError={costPricesQuery.error}
                            withCard={false}
                            extra={
                                <Button
                                    icon={<ReloadOutlined />}
                                    onClick={() => costPricesQuery.refetch()}
                                    loading={costPricesQuery.isFetching}
                                >
                                    Повторить
                                </Button>
                            }
                        />
                    ) : (
                        <Table<CostPriceType>
                            rowKey={(record) => record.id}
                            dataSource={costPrices}
                            columns={costPriceColumns}
                            pagination={false}
                            loading={isCostPriceTableLoading}
                            locale={{
                                emptyText: <Empty description="Нет данных по себестоимости" />,
                            }}
                            scroll={{ x: 720 }}
                            size="small"
                        />
                    )}
                </Card>

                <Card title="Доступность по складам" loading={detailQuery.isLoading}>
                    <Table<MerchantProductAvailability>
                        rowKey={(record) => record.id}
                        dataSource={detail?.availabilities ?? []}
                        columns={availabilityColumns}
                        pagination={false}
                        locale={{
                            emptyText: <Empty description="Нет данных по складам" />,
                        }}
                        scroll={{ x: 720 }}
                    />
                </Card>
                <Card title="Цены по городам" loading={detailQuery.isLoading}>
                    <Table<MerchantProductCityPrice>
                        rowKey={(record) => record.id}
                        dataSource={detail?.city_prices ?? []}
                        columns={cityPriceColumns}
                        pagination={false}
                        locale={{
                            emptyText: <Empty description="Нет цен по городам" />,
                        }}
                        scroll={{ x: 720 }}
                    />
                </Card>
            </Flex>
            {merchantProductId && (
                <CostPriceDrawer
                    open={isDrawerOpen}
                    onClose={handleDrawerClose}
                    merchantProductId={merchantProductId}
                    costPrice={selectedCostPrice}
                />
            )}
        </>
    );
};

export default MerchantProductDetailPage;
